import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserById, updateUserRole, restoreUser, deleteUser } from '../../services/userApi';
import { fetchHospitals } from '../../services/hospitalApi';
import type { Hospital as ApiHospital } from '../../services/hospitalApi/hospitalApi.types';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../../components/Snackbar/types';
import { UseUserDetailReturn, User, Hospital } from './types';

/**
 * API型のHospitalをコンポーネント型のHospitalに変換する関数
 */
const mapApiHospitalToComponentHospital = (apiHospital: ApiHospital): Hospital => ({
  id: typeof apiHospital.id === 'string' ? parseInt(apiHospital.id, 10) : apiHospital.id as number,
  name: apiHospital.name
});

export const useUserDetail = (currentUser: User): UseUserDetailReturn => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  
  const [user, setUser] = useState<User | null>(null);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<'delete' | 'restore' | null>(null);
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  // ユーザー情報と病院情報の取得
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        // IDが未定義の場合はエラー
        if (!id) {
          setError('ユーザーIDが未指定です');
          setLoading(false);
          return;
        }

        // ユーザーと病院データを並行して取得
        const [userData, apiHospitalData] = await Promise.all([
          getUserById(id),
          fetchHospitals()
        ]);
        
        // 病院データをコンポーネント型に変換
        const hospitalData = apiHospitalData.map(mapApiHospitalToComponentHospital);
        
        // 型アサーションで型を明示
        const typedUserData = userData as User;
        
        // 管理者の権限チェック - 自分の病院のユーザーのみ閲覧可能
        if (currentUser.hospital_id && typedUserData.hospital_id !== currentUser.hospital_id) {
          setError('このユーザーの詳細を閲覧する権限がありません');
          setLoading(false);
          return;
        }
        
        setUser(typedUserData);
        setHospitals(hospitalData);
        setSelectedRole(typedUserData.role);
      } catch (err: unknown) {
        console.error('ユーザー情報取得エラー:', err);
        // エラーオブジェクトの型チェックと処理
        if (err && typeof err === 'object' && 'response' in err && 
            err.response && typeof err.response === 'object' && 
            'status' in err.response && err.response.status === 404) {
          setError('ユーザーが見つかりませんでした');
        } else {
          setError('ユーザー情報の取得に失敗しました');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentUser]);

  // 病院名を取得するヘルパー関数
  const getHospitalName = (hospitalId: number | null): string => {
    if (!hospitalId) return '未設定';
    const hospital = hospitals.find(h => h.id === hospitalId);
    return hospital ? hospital.name : '不明';
  };

  // 日付フォーマット関数
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '未設定';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // ユーザーの役割を変更
  const handleRoleChange = async (): Promise<void> => {
    if (!user || user.role === selectedRole || actionInProgress) return;
    
    try {
      setActionInProgress(true);
      await updateUserRole(user.id.toString(), selectedRole as 'user' | 'admin');
      
      setUser({ ...user, role: selectedRole });
      showSnackbar('ユーザーの権限を更新しました', SnackbarType.SUCCESS);
    } catch (err: unknown) {
      console.error('権限変更エラー:', err);
      showSnackbar('権限の変更に失敗しました', SnackbarType.ERROR);
      setSelectedRole(user.role); // 元の値に戻す
    } finally {
      setActionInProgress(false);
    }
  };

  // ユーザーの削除
  const handleDeleteUser = async (): Promise<void> => {
    if (!user || actionInProgress) return;
    
    try {
      setActionInProgress(true);
      await deleteUser(user.id.toString());
      
      setUser({ ...user, deleted_at: new Date().toISOString() });
      showSnackbar('ユーザーを削除しました', SnackbarType.SUCCESS);
      setIsConfirmOpen(false);
    } catch (err: unknown) {
      console.error('ユーザー削除エラー:', err);
      showSnackbar('ユーザーの削除に失敗しました', SnackbarType.ERROR);
    } finally {
      setActionInProgress(false);
    }
  };

  // ユーザーの復元
  const handleRestoreUser = async (): Promise<void> => {
    if (!user || actionInProgress) return;
    
    try {
      setActionInProgress(true);
      await restoreUser(user.id.toString());
      
      setUser({ ...user, deleted_at: null });
      showSnackbar('ユーザーを復元しました', SnackbarType.SUCCESS);
      setIsConfirmOpen(false);
    } catch (err: unknown) {
      console.error('ユーザー復元エラー:', err);
      showSnackbar('ユーザーの復元に失敗しました', SnackbarType.ERROR);
    } finally {
      setActionInProgress(false);
    }
  };

  // 確認ダイアログを開く
  const openConfirmDialog = (action: 'delete' | 'restore'): void => {
    setConfirmAction(action);
    setIsConfirmOpen(true);
  };

  // 確認ダイアログを閉じる
  const closeConfirmDialog = (): void => {
    setIsConfirmOpen(false);
  };

  // ユーザー一覧へ戻る
  const navigateToUsers = (): void => {
    navigate('/users');
  };

  // 現在のユーザーと表示対象ユーザーが同じかを判定
  const isCurrentUser = currentUser.id === (user?.id ?? -1);
  
  // 現在のユーザーが管理者かどうか判定
  const isAdmin = currentUser.role === 'admin';

  return {
    user,
    hospitals,
    loading,
    error,
    selectedRole,
    isConfirmOpen,
    confirmAction,
    actionInProgress,
    setSelectedRole,
    handleRoleChange,
    handleDeleteUser,
    handleRestoreUser,
    openConfirmDialog,
    closeConfirmDialog,
    getHospitalName,
    formatDate,
    navigateToUsers,
    isCurrentUser,
    isAdmin
  };
}; 